/**
 * The small map + details card shown on a customer's page, in place of the old
 * "Directions" button. One shop, as a pin, with the facts a visitor needs — shop
 * name, contact, mobile, store type, orders this month, outstanding — and a
 * Navigate hand-off. Lives here (not in a screen) because every app that shows a
 * customer shows this: admin and field staff alike, from one component.
 */

import { Skeleton } from './ui';
import { MapCanvas, type MapMarker } from './MapCanvas';
import { navigationUrl } from '../lib/routing';
import { useQuery } from '../lib/useApi';
import { money } from '../lib/format';
import { displayPhone } from '../lib/phone';

interface ShopCard {
  shopId: string; shopName: string; tradeName: string | null;
  latitude: number | null; longitude: number | null; locationUrl: string | null;
  beatName: string | null; storeType: string | null; pocName: string | null;
  phone: string | null; accountPhone: string | null;
  outstandingPaise: number; ordersThisMonth: number;
  activity: 'active' | 'slowing' | 'dormant';
}

const ACTIVITY_COLOR: Record<ShopCard['activity'], string> = {
  active: '#15803d', slowing: '#b45309', dormant: '#9aa0a6',
};

export function ShopMiniMap({ shopId }: { shopId: string }) {
  const { data, loading } = useQuery<{ shop: ShopCard }>(`/api/map/shop/${shopId}`);
  const s = data?.shop;

  if (loading && !s) return <Skeleton rows={1} height={180} />;
  if (!s) return null;

  const details = (
    <div style={{ padding: '10px 12px' }}>
      <div className="t-semi">{s.shopName}{s.tradeName && s.tradeName !== s.shopName ? ` · ${s.tradeName}` : ''}</div>
      <table className="shopcard" style={{ fontSize: 'var(--fs-xs)', marginTop: 4 }}><tbody>
        <tr><td className="t-muted">Contact</td><td>{s.pocName || '—'}</td></tr>
        <tr><td className="t-muted">Mobile</td><td>
          {(s.phone || s.accountPhone)
            ? <a href={`tel:${s.phone || s.accountPhone}`}>{displayPhone(s.phone || s.accountPhone)}</a>
            : 'no phone'}
        </td></tr>
        <tr><td className="t-muted">Store type</td><td>{s.storeType || '—'}</td></tr>
        <tr><td className="t-muted">Orders this month</td><td>{s.ordersThisMonth}</td></tr>
        <tr><td className="t-muted">Outstanding</td><td><strong>{money(s.outstandingPaise)}</strong></td></tr>
      </tbody></table>
    </div>
  );

  // No location yet — the card still shows the facts, with a nudge instead of a map.
  if (s.latitude == null || s.longitude == null) {
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {details}
        <div className="t-xs t-muted" style={{ padding: '0 12px 12px' }}>
          No location pinned yet — use “Capture location” below to add it.
        </div>
      </div>
    );
  }

  const marker: MapMarker = {
    id: s.shopId, latitude: s.latitude, longitude: s.longitude,
    color: ACTIVITY_COLOR[s.activity], shape: 'pin',
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <MapCanvas className="mapcanvas mapcanvas--mini" markers={[marker]} />
      {details}
      <div style={{ padding: '0 12px 12px' }}>
        <a className="btn btn--sm" href={navigationUrl({ latitude: s.latitude, longitude: s.longitude, locationUrl: s.locationUrl })} target="_blank" rel="noreferrer">Navigate</a>
      </div>
    </div>
  );
}
